import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { agents, type Agent } from '@/lib/api';

interface CreateAgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  specialization: string;
  active: boolean;
  accepts_leads: boolean;
  max_leads_per_day: number | undefined;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  specialization: '',
  active: true,
  accepts_leads: true,
  max_leads_per_day: undefined,
};

export function CreateAgentModal({ open, onOpenChange }: CreateAgentModalProps) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<Agent>) => agents.create(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      handleClose();
    },
  });

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onOpenChange(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nafn er nauðsynlegt';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Netfang er nauðsynlegt';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Netfang er ekki gilt';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: Partial<Agent> = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || undefined,
      specialization: formData.specialization.trim() || undefined,
      active: formData.active,
      accepts_leads: formData.accepts_leads,
      max_leads_per_day: formData.max_leads_per_day || undefined,
    };

    createMutation.mutate(submitData);
  };

  const handleChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nýr söluaðili</DialogTitle>
          <DialogDescription>
            Skráðu nýjan söluaðila í kerfið. Reitir með stjörnu eru nauðsynlegir.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Nafn <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Jón Jónsson"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Netfang <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="jon@fasteignir.is"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Sími</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+354 555 1234"
            />
          </div>

          {/* Specialization */}
          <div className="space-y-2">
            <Label htmlFor="specialization">Sérhæfing</Label>
            <Input
              id="specialization"
              value={formData.specialization}
              onChange={(e) => handleChange('specialization', e.target.value)}
              placeholder="t.d. Íbúðir, Einbýlishús, Atvinnuhúsnæði"
            />
            <p className="text-xs text-gray-500">
              Tegund eigna sem söluaðili sérhæfir sig í
            </p>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 text-sm">Virkur</p>
              <p className="text-xs text-gray-500">
                Söluaðili getur skráð sig inn
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('active', !formData.active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.active ? 'bg-prima-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Accepts leads toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 text-sm">Tekur við ábendingum</p>
              <p className="text-xs text-gray-500">
                Ábendingum er úthlutað á söluaðila
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('accepts_leads', !formData.accepts_leads)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.accepts_leads ? 'bg-prima-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.accepts_leads ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Max leads per day */}
          {formData.accepts_leads && (
            <div className="space-y-2">
              <Label htmlFor="max_leads_per_day">Hámark ábendinga á dag</Label>
              <Input
                id="max_leads_per_day"
                type="number"
                min="1"
                max="100"
                value={formData.max_leads_per_day || ''}
                onChange={(e) =>
                  handleChange(
                    'max_leads_per_day',
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
                placeholder="Ótakmarkað"
              />
              <p className="text-xs text-gray-500">
                Skildu eftir autt fyrir engin takmörk
              </p>
            </div>
          )}

          {/* Error message from API */}
          {createMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : 'Villa kom upp við að skrá söluaðila'}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Hætta við
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Skrá söluaðila
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
